<template>
  <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
  <div class="relative z-10 flex items-center justify-between">
    <div class="flex space-x-4 items-center text-2xl font-medium">
      <ClipboardPaste color="black" />
      <div>
        {{ survey?.name }}
        {{ selected_response ? $t("Response for") + ` ${$utils.getRecordName(selected_response.record)}` : $t("Responses") }}
      </div>
    </div>
    <div
      class="w-8 h-8 ml-auto border border-gray-300 rounded-full cursor-pointer has-tooltip dark:border-dark-divider-border dark:bg-dark-screen-background"
      @click="$emit('cancel')">
      <X color="#FF0000" class="w-6 h-6 ml-[3px] mt-[3px] transition ease-in-out duration-side-menu-transition-duration" /><span
        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
        {{ $t("Close") }}</span>
    </div>
  </div>

  <div v-if="!selected_response" class="relative mt-10 flex items-stretch flex-grow focus-within:z-10">
    <div class="absolute z-10 inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
      <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
    </div>
    <TextInput
      input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
      name="task_name"
      :icon="true"
      type="text"
      :validate-on-input="true"
      :label="$t('Search by Recipient')"
      @update:modelValue="
        (e) => {
          search_term = e
        }
      " />

    <RotateCw
      :class="{
        'animate-spin': responses_loading,
        'opacity-100': responses_loading
      }"
      class="w-5 h-5 mr-3 -ml-1 opacity-0 absolute right-2 top-2"
      aria-hidden="true" />
  </div>

  <button v-if="selected_response" class="btn cancel-btn mt-6 w-fit mb-4" @click="selected_response = null">
    <ArrowRight class="w-5 h-5 mr-2" :class="[$store.state.portfolio.ui_options?.language == 'ar' ? '' : 'rotate-180']" />
    {{ $t("Back to Responses") }}
  </button>

  <div v-if="selected_response" class="w-full max-h-[800px] overflow-auto overflow-x-hidden mb-6">
    <SurveyQuestions :color="survey?.survey_page_header_color" :read-only="true" :questions-list="questions_list_clean" />
  </div>

  <div v-else class="w-full mt-6 max-h-[800px] overflow-auto mb-6">
    <table class="w-full table-fixed">
      <thead class="w-full text-md transition ease-in-out px-4 font-medium rounded-lg border text-gray-400 dark:text-dark-body-text">
        <tr>
          <td class="pr-6 pl-2 py-3 h-12 truncate">
            {{ $t("Recipient Name") }}
          </td>
          <td class="px-6 py-3 h-12 truncate">
            {{ $t("Company") }}
          </td>
          <td class="px-6 py-3 h-12 truncate">
            {{ $t("Response Date") }}
          </td>
          <td class="px-6 py-3 h-12 truncate">
            {{ $t("Survey") }}
          </td>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(response, index) in responses.results"
          :key="index"
          class="w-full text-sm transition ease-in-out rounded-lg border text-light-body-text dark:text-dark-body-text">
          <td class="px-6 py-3 h-12 truncate">
            <Avatar
              :hover-effect="true"
              :first-name="$utils.getRecordName(response.record).split(' ')[0]"
              :last-name="$utils.getRecordName(response.record).split(' ')[1]"
              class="group-hover/name:bg-cyan-500 mr-2" />

            {{ $utils.getRecordName(response.record) }}
          </td>
          <td class="px-6 py-3 h-12 truncate">
            {{ response.organization.name }}
          </td>
          <td class="px-6 py-3 h-12 truncate">
            {{ $moment(response.datetime_created).format("DD MMM  YYYY") }}
          </td>
          <td class="px-6 py-3 h-12 truncate">
            <button class="btn primary-btn" @click="selected_response = response">
              <View color="white" class="text-white mr-2 w-5 h-5" /> {{ $t("View Survey") }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div
    v-if="!selected_response"
    :class="{ 'w-[91%]': responses.results.length > 11 }"
    class="absolute border-none mb-4 py-1 bottom-2 w-[92%] h-16 overflow-visible dark:bg-dark-box-background">
    <PaginationFooter :default-page-size="page_size" :object="responses" @update="handlePaginationUpdate" />
  </div>
</template>

<script>
import SurveyQuestions from "@/components/shared/SurveyQuestions.vue"
import TextInput from "@/components/shared/TextInput.vue"
import { View, ClipboardPaste, Search, RotateCw, X, ArrowRight } from "lucide-vue-next"
import dataService from "@/services/data.service"
import Avatar from "@/components/shared/Avatar.vue"
import PaginationFooter from "@/components/shared/PaginationFooter.vue"

export default {
  components: {
    X,
    ClipboardPaste,
    View,
    ArrowRight,
    RotateCw,
    TextInput,
    Search,
    Avatar,
    PaginationFooter,
    SurveyQuestions
  },
  props: { survey: null },
  emits: ["cancel"],
  data() {
    return {
      search_term: "",
      responses: { results: [] },
      page_size: 30,
      responses_loading: false,
      search_timout: null,
      selected_response: null
    }
  },

  computed: {
    questions_list_clean() {
      const list_calc = []
      this.survey?.survey_layout?.forEach((layout_item) => {
        const question_obj_calc = {}
        if (
          layout_item.type == "text_response" ||
          layout_item.type == "text_area" ||
          layout_item.type == "url" ||
          layout_item.type == "nps" ||
          layout_item.type == "csat" ||
          layout_item.type == "number" ||
          layout_item.type == "star_rating" ||
          layout_item.type == "bar_rating"
        ) {
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          const response_found = this.selected_response.responses.find((q) => q.question == question_found.id)
          question_obj_calc["question_text"] = question_found?.metric_name
          question_obj_calc["question_id"] = question_found.id
          question_obj_calc["type"] = layout_item.type
          question_obj_calc["required"] = question_found.is_required
          question_obj_calc["value"] = response_found?.value || response_found?.value === 0 ? response_found.value : ""
          list_calc.push(question_obj_calc)
        }

        if (layout_item.type == "single_choice") {
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          const response_found = this.selected_response.responses.find((q) => q.question == question_found.id)

          question_obj_calc["question_text"] = question_found?.metric_name
          question_obj_calc["question_id"] = question_found.id
          question_obj_calc["choices"] = question_found?.metric_input_options.choices
          question_obj_calc["type"] = layout_item.type
          question_obj_calc["required"] = question_found.is_required
          question_obj_calc["value"] = response_found?.value ? response_found.value : ""
          list_calc.push(question_obj_calc)
        }

        if (layout_item.type == "multiple_choice") {
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          const response_found = this.selected_response.responses.find((q) => q.question == question_found.id)

          question_obj_calc["question_text"] = question_found?.metric_name
          question_obj_calc["question_id"] = question_found.id
          question_obj_calc["choices"] = question_found?.metric_input_options.choices
          question_obj_calc["type"] = layout_item.type
          question_obj_calc["required"] = question_found.is_required
          question_obj_calc["values"] = JSON.parse(response_found?.value?.length) ? JSON.parse(response_found?.value) : []
          list_calc.push(question_obj_calc)
        }

        if (layout_item.type == "single_choice_grid") {
          const first_question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.questions[0])
          question_obj_calc["question_text"] = first_question_found?.question
          question_obj_calc["choices"] = first_question_found?.metric_input_options.choices
          question_obj_calc["required"] = first_question_found.is_required

          const metrics_list_calc = []
          const metric_values_list_calc = []
          const ids_list = []
          layout_item.questions.forEach((layout_question) => {
            const found_question = this.survey.questions.find((q) => q.fe_uuid == layout_question)
            const response_found = this.selected_response.responses.find((q) => q.question == found_question.id)

            metrics_list_calc.push(found_question?.metric_name)
            metric_values_list_calc.push(response_found?.value ? response_found.value : "")
            ids_list.push(found_question.id)
          })
          question_obj_calc["metrics"] = metrics_list_calc
          question_obj_calc["type"] = layout_item.type
          question_obj_calc["values"] = metric_values_list_calc
          question_obj_calc["question_ids"] = ids_list
          list_calc.push(question_obj_calc)
        }

        if (layout_item.type == "multiple_choice_grid") {
          const first_question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.questions[0])
          question_obj_calc["question_text"] = first_question_found?.question
          question_obj_calc["choices"] = first_question_found?.metric_input_options.choices
          question_obj_calc["required"] = first_question_found.is_required

          const metrics_list_calc = []
          const metric_values_list_calc = []
          const ids_list = []
          layout_item.questions.forEach((layout_question) => {
            const found_question = this.survey.questions.find((q) => q.fe_uuid == layout_question)
            const response_found = this.selected_response.responses.find((q) => q.question == found_question.id)

            metrics_list_calc.push(found_question?.metric_name)
            metric_values_list_calc.push(JSON.parse(response_found?.value?.length) ? JSON.parse(response_found?.value) : [])
            ids_list.push(found_question.id)
          })
          question_obj_calc["metrics"] = metrics_list_calc
          question_obj_calc["type"] = layout_item.type
          question_obj_calc["values"] = metric_values_list_calc
          question_obj_calc["question_ids"] = ids_list
          list_calc.push(question_obj_calc)
        }

        if (layout_item.type == "date") {
          const question_found = this.survey.questions.find((q) => q.fe_uuid == layout_item.question)
          const response_found = this.selected_response.responses.find((q) => q.question == question_found.id)
          question_obj_calc["question_text"] = question_found?.metric_name
          question_obj_calc["question_id"] = question_found.id
          question_obj_calc["type"] = layout_item.type
          question_obj_calc["required"] = question_found.is_required
          question_obj_calc["value"] = response_found?.value ? response_found.value : ""
          list_calc.push(question_obj_calc)
        }
      })
      return list_calc
    }
  },

  watch: {
    search_term() {
      clearTimeout(this.search_timout)
      this.search_timout = setTimeout(() => {
        this.getResponses()
      }, 500)
    }
  },
  created() {
    this.getResponses()
  },
  methods: {
    handlePaginationUpdate({ page, page_size }) {
      this.getResponses(page, page_size)
      this.page_size = page_size
    },

    getResponses(page = 1, page_size = this.page_size) {
      this.responses_loading = true
      const params = { page, page_size }
      if (this.search_term) {
        params["search"] = this.search_term
      }
      dataService.getSurveyResponses(this.survey.id, params).then((result) => {
        this.responses = result.data
        this.responses_loading = false
      })
    }
  }
}
</script>

<style scoped>
/* Style the table header */
.table-fixed thead {
  position: sticky;
  top: -1px;
  background-color: white;
  z-index: 1;
}
</style>
